<template>
  <RevCardCarousel
    :center="false"
    data-qa="card-carousel"
    :next-alternative-text="i18n(translations.alternativeNext)"
    :prev-alternative-text="i18n(translations.alternativePrevious)"
    :subtitle
    :title
  >
    <div
      v-for="(product, index) in products"
      :key="product.title"
      :ref="setupViewportTrackingProduct(product, tracking)"
      class="relative h-full"
    >
      <ProductCard
        :color-swatches="
          colorSwatches(product.specifications, productGranularity)
        "
        data-test="product-card"
        :deal="product.deal"
        :description="
          description(
            product,
            productGranularity,
            i18n(translations.specialOfferType7),
          )
        "
        :discounted-price="product.discountedPrice"
        :extended-description="extendedDescription(productGranularity)"
        :granularity="productGranularity"
        :image-alt="product.title"
        :image-src="product.image"
        :list="tracking.list"
        :listing-id="product.listingId"
        :position="index + 1"
        :price="product.price"
        :price-new="product.priceNew"
        :product
        :review-rating="product.reviewRating"
        :tags="product.marketingTags"
        :title="product.title"
        :url="buildProductPageLink(product)"
        :with-starting-from="withStartingFrom(productGranularity)"
        v-on="addToCartListener ? { addToCart: handleAddToCart(product) } : {}"
        @link-click="
          trackProductClick(product, {
            granularity: productGranularity,
            ...tracking,
            position: index + 1,
            source: 'recommendation',
          })
        "
      />
      <slot :index name="post-card" :product />
    </div>

    <template v-if="cta" #button>
      <RevButton
        :icon="IconArrowRight"
        :rel="cta?.link?.rel"
        size="medium"
        :target="cta?.link?.target"
        :to="cta?.link?.href"
        variant="primary"
      >
        {{ cta?.label }}
      </RevButton>
    </template>
  </RevCardCarousel>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'

import type { CTALink } from '@backmarket/http-api/src/api-specs-content/models/cta-link'
import type { ProductGranularity } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { IconArrowRight } from '@ds/icons/IconArrowRight'

import ProductCard from '@backmarket/nuxt-layer-recommendation/ProductCard.vue'

import { useProductPageLink } from '../../composables/useProductPageLink'
import {
  type ExtraTrackingData,
  useClickTracking,
  useImpressionTracking,
} from '../../composables/useProductTracking'
import type { Product } from '../../models/product'

import translations from './ProductCardCarousel.translations'
import {
  colorSwatches,
  description,
  extendedDescription,
  withStartingFrom,
} from './ProductCardVersion.helper'

const i18n = useI18n()

const props = withDefaults(
  defineProps<{
    products: Product[]
    tracking?: ExtraTrackingData
    useClientSideNavigation?: boolean
    title?: string
    subtitle?: string
    cta?: CTALink
    productGranularity?: ProductGranularity
  }>(),
  {
    tracking: () => ({}),
    useClientSideNavigation: true,
    title: undefined,
    subtitle: undefined,
    cta: undefined,
    productGranularity: undefined,
  },
)

const emits = defineEmits<{
  (e: 'add-to-cart', data: { status: string; product: Product }): void
}>()

const { buildProductPageLink } = useProductPageLink({
  useClientSideNavigation: props.useClientSideNavigation,
})
const addToCartListener = getCurrentInstance()?.vnode.props?.onAddToCart

const { setupViewportTrackingProduct } = useImpressionTracking()
const { trackProductClick } = useClickTracking()

function handleAddToCart(product: Product) {
  return ({ status }: { status: string }) => {
    emits('add-to-cart', { status, product })
  }
}
</script>
